import { c as _c } from "react-compiler-runtime";
import { useEffect, useRef } from 'react';
import { VIRTUAL_INDEX_ATTRIBUTE } from '../constants/virtualization';
export function isNestedRow(node) {
  return node instanceof Element && node.tagName === 'TR' && !node.hasAttribute(VIRTUAL_INDEX_ATTRIBUTE) && node.getAttribute('aria-hidden') !== 'true';
}
function findParentRow(nestedRow) {
  let el = nestedRow.previousElementSibling;
  while (el) {
    if (el.hasAttribute(VIRTUAL_INDEX_ATTRIBUTE)) {
      return el;
    }
    el = el.previousElementSibling;
  }
  return null;
}

// Consumers can render nested rows as sibling <tr> elements without a
// `data-fdt-index` attribute (e.g. inline association tables). The virtualizer's
// built-in ResizeObserver only watches the primary <tr> for each row, so it
// never notices when these nested rows are added, removed, or change height.
//
// This hook bridges that gap by attaching two observers to the <tbody>:
//   1. A MutationObserver that detects when nested <tr>s are inserted or
//      removed, then schedules a remeasure of the parent virtualised row.
//   2. A single ResizeObserver that watches every nested <tr> for height
//      changes (e.g. async data load, pagination inside an inline
//      association table), again triggering a remeasure of the parent row.
//
// All remeasure calls are batched into a single requestAnimationFrame to
// avoid layout thrashing when many nested rows change in the same tick.
export const useNestedRowObservers = virtualizer => {
  const $ = _c(3);
  const tbodyRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== virtualizer) {
    t0 = () => {
      const tbody = tbodyRef.current;
      if (!tbody) {
        return;
      }
      let rafId = null;
      const rowsToRemeasure = new Set();
      const flushRemeasure = () => {
        rafId = null;
        for (const row of rowsToRemeasure) {
          virtualizer.measureElement(row);
        }
        rowsToRemeasure.clear();
      };
      const scheduleRemeasure = parentRow => {
        rowsToRemeasure.add(parentRow);
        if (rafId === null) {
          rafId = requestAnimationFrame(flushRemeasure);
        }
      };
      const resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          const parentRow_0 = findParentRow(entry.target);
          if (parentRow_0) {
            scheduleRemeasure(parentRow_0);
          }
        }
      });
      const mutationObserver = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (!isNestedRow(node)) {
              continue;
            }
            resizeObserver.observe(node);
            const parentRow_1 = findParentRow(node);
            if (parentRow_1) {
              scheduleRemeasure(parentRow_1);
            }
          }
          for (const node_0 of mutation.removedNodes) {
            if (isNestedRow(node_0)) {
              resizeObserver.unobserve(node_0);
            }
          }
          if (mutation.removedNodes.length > 0) {
            var _mutation$previousSib;
            const anchor = (_mutation$previousSib = mutation.previousSibling) !== null && _mutation$previousSib !== void 0 ? _mutation$previousSib : mutation.nextSibling;
            if (anchor instanceof Element) {
              const parentRow_2 = anchor.hasAttribute(VIRTUAL_INDEX_ATTRIBUTE) ? anchor : findParentRow(anchor);
              if (parentRow_2) {
                scheduleRemeasure(parentRow_2);
              }
            }
          }
        }
      });
      mutationObserver.observe(tbody, {
        childList: true
      });
      return () => {
        mutationObserver.disconnect();
        resizeObserver.disconnect();
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
      };
    };
    t1 = [virtualizer];
    $[0] = virtualizer;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return tbodyRef;
};